/**
 * UltamateRewardsOffers is consist of LargedealRail and manages different states
 *
 * @module views/components/UltamateRewardsOffers
 * @memberof -Common
 */

import './UltamateRewardsOffers.scss';

import React, { useEffect, useRef } from 'react';

import PropTypes from 'prop-types';
import { SwiperSlide } from 'swiper/react';

import Rail from '@ulta/core/components/Rail/Rail';
import { hasItems } from '@ulta/core/utils/array/array';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import MediumDealCard from '@ulta/modules/MediumDealCard/MediumDealCard';

import { constants } from '@ulta/utils/constants/constants';

import DealsRail from '../../components/DealsRail/DealsRail';

/**
   * Represents a UltamateRewardsOffers component
   *
   * @method
   * @param {UltamateRewardsOffersProps} props - React properties passed from composition
   * @returns UltamateRewardsOffers
   */
export const UltamateRewardsOffers = function( props ){
  const {
    offers,
    invokeMutation,
    nextClickAction,
    variantType,
    previousClickAction,
    titleText = defaultProps.titleText,
    title,
    setShowSpacer,
    viewAllOffersAction,
    _railADAmeta,
    _railMeta
  } = props;

  const railRef = useRef( null );

  useEffect( ()=>{
    if( setShowSpacer ){
      setShowSpacer( hasItems( offers ) );
    }
  }, [offers] );

  if( !hasItems( offers ) ){
    return null;
  }

  // temporary title object for backwards compatibility
  // if titleText is not used, fallback to title string
  const headerTitle = {
    text: titleText?.text || title,
    htmlTag: titleText?.htmlTag || defaultProps.titleText.htmlTag
  };

  const updatedProps = Object.assign( { headerTitle }, props );

  return (
    <div className='UltamateRewardsOffers'
      ref={ railRef }
    >
      { variantType === ULTAMATE_REWARDS_RAIL_VIEW_OPTION?.DEALS ? (
        <DealsRail { ...spreadProps( { ...updatedProps } ) } />
      ) : (
        <Rail
          railRef={ railRef }
          items={ offers }
          headline={ headerTitle.text }
          headlineTag={ headerTitle.htmlTag }
          previousClickAction={ previousClickAction }
          nextClickAction={ nextClickAction }
          viewAllAction={ viewAllOffersAction }
          showBullets={ true }
          sliderPerViewXL={ 2.25 }
          slidesPerGroupXL={ 2 }
          sliderPerViewLG={ 2.75 }
          slidesPerGroupLG={ 2 }
          sliderPerViewMD={ 2.35 }
          slidesPerGroupMD={ 2 }
          sliderPerViewSM={ 1.10 }
          slidesPerGroupSM={ 1 }
          _railADAmeta={ _railADAmeta }
          _railMeta={ _railMeta }
        >

          {
            offers.length !== 1 && offers?.map( ( offer, cardsIndex ) => (
              <SwiperSlide key={ `UltamateRewardsOffers__Rail${ cardsIndex + 1 } ` }>
                <MediumDealCard
                  key={ cardsIndex }
                  action1={ offer.actionGroup.action1 }
                  action2={ offer.actionGroup.action2 }
                  action1Style={ offer.actionGroup.action1Style }
                  action2Style={ offer.actionGroup.action2Style }
                  eyebrow={ offer.eyebrow }
                  headline={ offer.headline }
                  image={ offer.image }
                  isOfferActivated={ offer.isOfferActivated }
                  tag={ offer.tag }
                  urgencyTag={ offer.urgencyTag }
                  subHeadline={ offer.description }
                  activateAction={ offer.activateAction }
                  invokeMutation={ invokeMutation }
                  offerId={ offer.offerId }
                />
              </SwiperSlide>
            ) ) }

        </Rail>
      ) }
    </div>
  );
};

/**
 * @const {string} ULTAMATE_REWARDS_RAIL_VIEW_OPTION - option for horizontal rail view
 */
export const ULTAMATE_REWARDS_RAIL_VIEW_OPTION = {
  DEALS: 'mediumDealCard'
  // NOTE: beware backwards compatibility issues with the OFFERS variantType. DXL only sends this variantType prop for our new DEALS variant type.
};

/**
   * Default values for passed properties
   * @type {object}
   */
export const defaultProps = {
  ...constants.INTERSECTION_OBSERVER_OPTIONS,
  titleText: {
    htmlTag: 'h2'
  }
};

/**
   * Property type definitions
   * @typedef UltamateRewardsOffersProps
   * @type {object}
   * @property {array} offers - Set the reward offers object
   * @property {string} variantType - set the offer title
  */

export const propTypes = {
  offers: PropTypes.array,
  variantType: PropTypes.string
};

UltamateRewardsOffers.propTypes = propTypes;
UltamateRewardsOffers.defaultProps = defaultProps;

export default UltamateRewardsOffers;